import { type BaseButtonProps, Button } from "@msuite/ui/button";
import { useTranslation } from "@msuite/ui/localization";
import type { PropsWithChildren } from "react";
import { Modal } from "../modal";
import type { AsyncModalController } from "../use-async-modal";

/** Props Interface */
interface ConfirmModalProps<T = boolean> extends PropsWithChildren {
	controller: AsyncModalController<T>;
	message: string | null;
	confirmLabel?: string;
	rejectLabel?: string;
	closeOnEsc?: boolean;
	confirmVariant?: BaseButtonProps["variant"];
	onResolve?: () => T | Promise<T | boolean>;
	confirmIsDisabled?: boolean;
	title?: string;
	"data-cy"?: string;
	rejectButtonProps?: BaseButtonProps;
	confirmButtonProps?: BaseButtonProps;
	width?: string;
}

export function ConfirmModal<T = boolean>({
	controller,
	message,
	confirmLabel,
	rejectLabel,
	closeOnEsc,
	confirmVariant = "danger",
	children,
	onResolve: _onResolve,
	confirmIsDisabled = false,
	title,
	"data-cy": dataCy,
	width,

	confirmButtonProps = {},
	rejectButtonProps = {},
}: ConfirmModalProps<T>) {
	/** Hooks */
	const { t } = useTranslation();

	/** Functions */
	function handleOnReject() {
		controller.onResolve(false);
	}

	async function handleOnConfirm() {
		controller.onResolve((await _onResolve?.()) || true);
	}

	/** Computed */
	const confirmText = confirmLabel || t("general.confirm");
	const rejectText = rejectLabel || t("general.cancel");

	/** Render */
	return (
		<Modal
			isOpen={controller.isOpen}
			onClose={handleOnReject}
			title={title || t("general.confirm")}
			closeOnEsc={closeOnEsc}
			data-cy={dataCy}
			onSubmit={handleOnConfirm}
			withWarning={false}
			width={width}
		>
			<Modal.Body>
				{controller.renderRef.current ? (
					controller.renderRef.current
				) : controller.messageRef.current ? (
					<p className="text-color">{controller.messageRef.current}</p>
				) : message ? (
					<p className="text-color">{message}</p>
				) : null}
				{children}
			</Modal.Body>
			<Modal.Footer>
				<Button
					variant="ghost"
					onClick={handleOnReject}
					autoFocus
					data-cy="confirm-modal-reject-button"
					{...rejectButtonProps}
				>
					{rejectText}
				</Button>
				<Button
					variant={confirmVariant}
					onClick={handleOnConfirm}
					isDisabled={confirmIsDisabled}
					data-cy="confirm-modal-confirm-button"
					{...confirmButtonProps}
				>
					{confirmText}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
